<template>
  <AnnDialog
    :model-value="props.modelValue"
    @update:modelValue="emit('update:modelValue', $event)"
  >
    <div class="ann-elite-rally">
      <img
        class="ann-elite-rally-bg"
        :src="backgroundImg"
        alt=""
        aria-hidden="true"
      />
      <img
        class="ann-elite-rally-title"
        :src="titleImg"
        :alt="t('ann2026.contribute.tabRally')"
      />
      <div class="ann-elite-rally-subtitle">{{ t('ann2026.eliteRally.subtitle') }}</div>

      <div class="ann-elite-rally-scroll">
        <div class="ann-elite-rally-list">
          <div
            v-for="(card, cardIndex) in displayCards"
            :key="card.rewardCode"
            class="ann-elite-rally-card"
            :style="cardPosition(cardIndex)"
          >
            <img
              class="ann-elite-rally-card-bg"
              :src="cardBackground(cardIndex)"
              alt=""
              aria-hidden="true"
            />
            <div class="ann-elite-rally-label">
              {{ inviteLabel(card.needCount) }}
            </div>

            <div
              v-for="(item, itemIndex) in card.items"
              :key="item.key"
              class="ann-elite-rally-item"
              :class="{ 'is-multi': card.items.length > 1 }"
              :style="itemPosition(card.items.length, itemIndex)"
            >
              <img
                class="ann-elite-rally-item-frame"
                :src="itemFrameImg"
                alt=""
                aria-hidden="true"
              />
              <img
                v-if="item.icon"
                class="ann-elite-rally-item-icon"
                :src="item.icon"
                :alt="item.name"
              />
              <div class="ann-elite-rally-item-name">{{ item.name }}</div>
            </div>

            <button
              class="ann-elite-rally-status"
              :class="{ 'is-claim': card.status === 1 }"
              type="button"
              :disabled="card.status !== 1"
              @click="emit('receive', card.rewardCode)"
            >
              <img
                :src="card.status === 1 ? buttonActiveImg : buttonDisabledImg"
                alt=""
                aria-hidden="true"
              />
              <span>{{ statusText(card.status) }}</span>
            </button>
          </div>
        </div>
      </div>

      <img
        class="ann-elite-rally-character"
        :src="characterImg"
        alt=""
        aria-hidden="true"
      />

      <AnnDialogClose
        :aria-label="t('ann2026.dialog.close')"
        @close="emit('update:modelValue', false)"
      />
    </div>
  </AnnDialog>
</template>

<script setup>
import { computed, defineEmits, defineProps } from 'vue'
import { useI18n } from 'vue-i18n'
import AnnDialog from '../AnnDialog/index.vue'
import AnnDialogClose from '../AnnDialogClose/index.vue'
import { getItemIcon, isItemId } from '../../item'
import {
  getItemName,
  getRewardCount,
  getRewardId,
  parseProps,
} from '../../utils/reward'
import backgroundImg from '@/assets/imgs/act/ann2026/popups/elite-rally/background.png'
import titleZh from '@/assets/imgs/act/ann2026/popups/elite-rally/title/zh.png'
import titleEn from '@/assets/imgs/act/ann2026/popups/elite-rally/title/en.png'
import titleVi from '@/assets/imgs/act/ann2026/popups/elite-rally/title/vi.png'
import titleId from '@/assets/imgs/act/ann2026/popups/elite-rally/title/id.png'
import titleTh from '@/assets/imgs/act/ann2026/popups/elite-rally/title/th.png'
import cardBackgroundFirstImg from '@/assets/imgs/act/ann2026/popups/elite-rally/card-background-first.png'
import cardBackgroundThirdImg from '@/assets/imgs/act/ann2026/popups/elite-rally/card-background-third.png'
import itemFrameImg from '@/assets/imgs/act/ann2026/popups/elite-rally/item-frame.png'
import buttonDisabledImg from '@/assets/imgs/act/ann2026/popups/elite-rally/button-disabled.png'
import buttonActiveImg from '@/assets/imgs/act/ann2026/popups/elite-rally/button-active.png'
import characterImg from '@/assets/imgs/act/ann2026/popups/elite-rally/character.png'
import { RECALL_CARDS } from '../../const'

const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false,
  },
  recallInfo: {
    type: Object,
    default: () => ({}),
  },
})

const emit = defineEmits(['update:modelValue', 'receive'])

const { t, te, locale } = useI18n({ useScope: 'global' })

const titleImg = computed(() => {
  switch (locale.value) {
    case 'hk':
    case 'tw':
      return titleZh
    case 'vi':
      return titleVi
    case 'id':
      return titleId
    case 'th':
      return titleTh
    default:
      return titleEn
  }
})

const resolveRewardItems = (apiReward, fallbackRewards) => {
  for (const field of [
    apiReward?.rewardItems,
    apiReward?.rewards,
    apiReward?.items,
    apiReward?.itemList,
    apiReward?.propSnapshot,
  ]) {
    const parsed = parseProps(field)
    if (parsed.length) return parsed
  }
  return fallbackRewards || []
}

const normalizeItem = (item, index) => {
  const id = getRewardId(item)
  const name =
    item.name ||
    item.itemName ||
    item.rewardName ||
    getItemName(id, t, te) ||
    t('ann2026.eliteRally.itemName')
  const count = getRewardCount(item)
  return {
    key: `${id || 'item'}-${index}`,
    name: `${name} ×${count}`,
    icon:
      item.iconUrl ||
      item.itemIcon ||
      item.icon ||
      (isItemId(id) ? getItemIcon(id) : null),
  }
}

const displayCards = computed(() => {
  const apiList = props.recallInfo?.rewardList || []
  return RECALL_CARDS.map((card) => {
    const api =
      apiList.find((item) => item.rewardCode === card.rewardCode) || {}
    const items = resolveRewardItems(api, card.rewards)
      .slice(0, 2)
      .map(normalizeItem)
    return {
      rewardCode: card.rewardCode,
      needCount: api.needCount ?? card.needCount,
      status: api.status ?? 0,
      items,
    }
  })
})

const inviteLabel = (count) =>
  Number(count) === 1
    ? t('ann2026.eliteRally.inviteOne')
    : t('ann2026.eliteRally.inviteMany', { count: count ?? 0 })

const statusText = (status) => {
  if (status === 2) return t('ann2026.eliteRally.claimed')
  if (status === 1) return t('ann2026.eliteRally.claim')
  return t('ann2026.eliteRally.locked')
}

const rem = (px) => `${+(px / 75.2).toFixed(5)}rem`

const cardPosition = (index) => ({
  top: rem(index * 248),
})

const itemPosition = (count, index) => {
  const lefts = count === 1 ? [140] : [40, 244]
  return {
    left: rem(lefts[index]),
  }
}

const cardBackground = (index) => {
  // TODO: 第二张卡背景切图缺失，当前临时复用第一张卡背景。
  if (index === 2) return cardBackgroundThirdImg
  return cardBackgroundFirstImg
}
</script>

<style lang="less" scoped>
.ann-elite-rally {
  position: relative;
  width: 701px;
  height: 1001px;
  overflow: visible;
}

.ann-elite-rally-bg {
  position: absolute;
  top: 0;
  left: 0;
  width: 701px;
  height: 1001px;
  pointer-events: none;
}

.ann-elite-rally-title {
  position: absolute;
  // 设计意图：标题向上探出弹窗顶部边缘。
  top: -20px;
  left: 50%;
  z-index: 5;
  display: block;
  // 图片标题锁宽跟随缩放，height auto 保持各语言切图等比不变形。
  // width: 7rem;
  max-width: 550px;
  // height: auto;
  height: 100px;
  object-fit: contain;
  transform: translateX(-50%);
}

.ann-elite-rally-subtitle {
  position: absolute;
  top: 138px;
  left: 104px;
  z-index: 5;
  width: 484px;
  height: 20px;
  overflow: hidden;
  color: #f44382;
  font-size: 18px;
  line-height: 20px;
  font-weight: 700;
  text-align: center;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.ann-elite-rally-scroll {
  position: absolute;
  top: 168px;
  left: 0;
  z-index: 2;
  width: 701px;
  height: auto;
  padding-bottom: 16px;
  box-sizing: border-box;
  overflow: visible;
}

.ann-elite-rally-list {
  position: relative;
  width: 701px;
  height: 736px;
}

.ann-elite-rally-card {
  position: absolute;
  left: 147px;
  z-index: 2;
  width: 410.9px;
  height: 235.4px;
}

.ann-elite-rally-card-bg {
  position: absolute;
  top: 0;
  left: 0;
  width: 410.9px;
  height: 240px;
  pointer-events: none;
}

.ann-elite-rally-label {
  position: absolute;
  top: 15px;
  left: 0;
  width: 410.9px;
  height: 20px;
  color: #fff;
  font-size: 18px;
  line-height: 20px;
  font-weight: 700;
  text-align: center;
  white-space: nowrap;
}

.ann-elite-rally-item {
  position: absolute;
  top: 36px;
  width: 126px;
  height: 150px;
}

.ann-elite-rally-item-frame {
  position: absolute;
  top: 0;
  left: 0;
  width: 126px;
  height: 126px;
}

.ann-elite-rally-item-icon {
  position: absolute;
  top: 18px;
  left: 18px;
  width: 90px;
  height: 90px;
  object-fit: contain;
}

.ann-elite-rally-item-name {
  position: absolute;
  top: 132px;
  // left: -13px;
  left: 50%;
  transform: translateX(-50%);
  height: 16px;
  overflow: hidden;
  color: #fff;
  font-size: 15px;
  font-weight: 700;
  text-align: center;
  white-space: nowrap;
}

.ann-elite-rally-item.is-multi .ann-elite-rally-item-name {
  top: 128px;
  display: -webkit-box;
  width: 126px;
  height: 48px;
  overflow: hidden;
  font-size: 12px;
  line-height: 16px;
  white-space: normal;
  overflow-wrap: anywhere;
  word-break: break-word;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
}

.ann-elite-rally-status {
  position: absolute;
  top: 186px;
  left: 155px;
  width: 101.2px;
  height: 35.8px;
  margin: 0;
  padding: 0;
  border: 0;
  background: transparent;

  &:disabled {
    opacity: 1;
  }

  &.is-claim {
    cursor: pointer;
  }

  img {
    position: absolute;
    inset: 0;
    width: 101.2px;
    height: 35.8px;
  }

  span {
    position: absolute;
    top: 12px;
    left: 8px;
    width: 85px;
    height: 14px;
    overflow: hidden;
    color: #fff;
    font-size: 13px;
    line-height: 14px;
    font-weight: 700;
    text-align: center;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &.is-claim span {
    color: #352670;
  }
}

.ann-elite-rally-character {
  position: absolute;
  bottom: -7px;
  left: -19px;
  z-index: 4;
  width: 183px;
  height: 170px;
  pointer-events: none;
}
</style>
